// 交易对话框 - 通用买卖弹窗
import { useState, useEffect, useMemo } from 'react';
import { Modal, InputNumber, Input, Space, Typography, Alert, Tag, Statistic, Divider } from 'antd';
import { useStore } from '../store/account';
import type { StockQuote, MarketType } from '../../shared/types';
const { Text } = Typography;
interface Props {
  open: boolean;
  mode: 'buy' | 'sell';
  quote: StockQuote | null;
  onClose: (refreshed?: boolean) => void;
}
export default function TradeDialog({ open, mode, quote, onClose }: Props) {
  const { account, refreshAccount, buy, sell } = useStore();
  const [priceType, setPriceType] = useState<'market' | 'limit'>('market');
  const [price, setPrice] = useState<number>(0);
  const [shares, setShares] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open && quote) {
      setPrice(quote.price);
      setShares(mode === 'buy' ? 100 : 0);
      setPriceType('market');
      setError(null);
    }
  }, [open, quote, mode]);
  const position = useMemo(() => {
    if (!quote || !account) return null;
    return account.positions.find((p) => p.code === quote.code);
  }, [account, quote]);
  const totalCost = price * shares;
  const availableCash = account?.cash ?? 0;
  const cashEnough = totalCost <= availableCash;
  const sharesEnough = !position || shares <= position.shares;
  const step = quote?.type === 'stock' ? 100 : 1;
  const minShares = quote?.type === 'stock' ? 100 : 1;
  const handleConfirm = async () => {
    if (!quote) return;
    if (price <= 0) {
      setError('价格必须 > 0');
      return;
    }
    if (shares < minShares) {
      setError(
        `${quote.type === 'stock' ? 'A 股' : '币种'}最少 ${minShares} ${quote.type === 'stock' ? '股' : '枚'}`
      );
      return;
    }
    if (mode === 'buy' && !cashEnough) {
      setError(
        `现金不足：需要 ¥${totalCost.toLocaleString()}，可用 ¥${availableCash.toLocaleString()}`
      );
      return;
    }
    if (mode === 'sell' && !sharesEnough) {
      setError(`股数不足：最多可卖 ${position?.shares ?? 0}`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r =
        mode === 'buy'
          ? await buy({ code: quote.code, name: quote.name, type: quote.type, price, shares })
          : await sell({ code: quote.code, price, shares });
      if (r.ok) {
        await refreshAccount();
        onClose(true);
      } else {
        setError(r.message);
      }
    } finally {
      setLoading(false);
    }
  };
  if (!quote) return null;
  const isStock = quote.type === 'stock';
  return (
    <Modal
      title={
        <Space>
          {' '}
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {' '}
            {mode === 'buy' ? '🟢 买入' : '🔴 卖出'} {quote.name}{' '}
          </span>{' '}
          <Tag color={isStock ? 'blue' : 'gold'}>{quote.code}</Tag>{' '}
        </Space>
      }
      open={open}
      onCancel={() => onClose(false)}
      onOk={handleConfirm}
      okText={`确认${mode === 'buy' ? '买入' : '卖出'}`}
      okButtonProps={{
        danger: mode === 'buy',
        loading,
        disabled: shares < minShares || price <= 0,
      }}
      cancelText="取消"
      width={480}
      destroyOnClose
    >
      {' '}
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {' '}
        <div style={{ background: '#1c2128', padding: 12, borderRadius: 6 }}>
          {' '}
          <Space size="large">
            {' '}
            <Statistic
              title="现价"
              value={quote.price}
              precision={2}
              valueStyle={{ fontSize: 18, color: '#e6edf3' }}
            />{' '}
            <Statistic
              title="涨跌幅"
              value={quote.changePercent}
              precision={2}
              suffix="%"
              valueStyle={{
                fontSize: 18,
                color:
                  quote.changePercent > 0
                    ? '#ff4d4f'
                    : quote.changePercent < 0
                      ? '#52c41a'
                      : '#e6edf3',
              }}
            />{' '}
          </Space>{' '}
        </div>{' '}
        {position && (
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                {' '}
                <span>当前持仓: {position.shares} 股</span>{' '}
                <span>成本: ¥{position.costPrice.toFixed(2)}</span>{' '}
                <span style={{ color: position.profitPercent >= 0 ? '#ff4d4f' : '#52c41a' }}>
                  {' '}
                  浮盈: {position.profitPercent >= 0 ? '+' : ''}{' '}
                  {(position.profitPercent * 100).toFixed(2)}%{' '}
                </span>{' '}
              </Space>
            }
          />
        )}{' '}
        <div>
          {' '}
          <Text type="secondary" style={{ fontSize: 12 }}>
            价格类型
          </Text>{' '}
          <div style={{ marginTop: 4 }}>
            {' '}
            <Space>
              {' '}
              <Tag.CheckableTag
                checked={priceType === 'market'}
                onChange={() => {
                  setPriceType('market');
                  setPrice(quote.price);
                }}
              >
                {' '}
                市价{' '}
              </Tag.CheckableTag>{' '}
              <Tag.CheckableTag
                checked={priceType === 'limit'}
                onChange={(v) => setPriceType(v ? 'limit' : 'market')}
              >
                {' '}
                限价{' '}
              </Tag.CheckableTag>{' '}
            </Space>{' '}
          </div>{' '}
        </div>{' '}
        <div>
          {' '}
          <Text type="secondary" style={{ fontSize: 12 }}>
            价格 (¥)
          </Text>{' '}
          <InputNumber
            value={price}
            onChange={(v) => setPrice(Number(v) || 0)}
            disabled={priceType === 'market'}
            style={{ width: '100%' }}
            min={0}
            step={isStock ? 0.01 : 1}
            precision={isStock ? 2 : 2}
            addonAfter="¥"
          />{' '}
        </div>{' '}
        <div>
          {' '}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {' '}
            数量 ({isStock ? '100 股起' : '整数'}){' '}
          </Text>{' '}
          <InputNumber
            value={shares}
            onChange={(v) => setShares(Number(v) || 0)}
            style={{ width: '100%' }}
            min={0}
            step={step}
            addonAfter={isStock ? '股' : '枚'}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />{' '}
          {isStock && (
            <Space size={4} style={{ marginTop: 4 }}>
              {' '}
              {[100, 300, 500, 1000].map((s) => (
                <Tag key={s} style={{ cursor: 'pointer' }} onClick={() => setShares(s)}>
                  {' '}
                  {s}股{' '}
                </Tag>
              ))}{' '}
            </Space>
          )}{' '}
        </div>{' '}
        <Divider style={{ margin: '4px 0' }} />{' '}
        <div>
          {' '}
          <Space size="large">
            {' '}
            <Statistic
              title="合计"
              value={totalCost}
              precision={2}
              prefix="¥"
              valueStyle={{ fontSize: 18, color: mode === 'buy' ? '#ff4d4f' : '#52c41a' }}
            />{' '}
            <div>
              {' '}
              <div style={{ fontSize: 12, color: '#8b949e' }}>可用现金</div>{' '}
              <div style={{ fontSize: 18, fontWeight: 600 }}>¥{availableCash.toLocaleString()}</div>{' '}
              {mode === 'buy' && !cashEnough && totalCost > 0 && (
                <Text type="danger" style={{ fontSize: 11 }}>
                  {' '}
                  现金不足 ¥{(totalCost - availableCash).toLocaleString()}{' '}
                </Text>
              )}{' '}
            </div>{' '}
          </Space>{' '}
        </div>{' '}
        {error && <Alert type="error" message={error} showIcon />}{' '}
        {mode === 'buy' && account && account.task1CountToday < 1 && (
          <Alert type="success" message={`完成本笔买入将获得任务 1 奖励 +¥10,000`} showIcon />
        )}{' '}
      </Space>{' '}
    </Modal>
  );
}
